<script setup>
import getRoute from '@/Common/GetRoute.js';
import SiteHeader from '@/Layouts/Partials/SiteHeader.vue';
import DropdownLink from '@/Components/Dropdown/DropdownLink.vue';
import SiteContainer from '@/Layouts/Partials/SiteContainer.vue';
import Banner from '@/Components/Banner.vue';
import NavBarItem from '@/Components/Nav/NavBarItem.vue';
import { Head, usePage } from '@inertiajs/vue3';
import Dropdown from '@/Components/Dropdown/Dropdown.vue';
import Footer from '@/Layouts/Partials/Footer.vue';
import NavBar from '@/Components/Nav/NavBar.vue';
import LanguageDropdown from '@/Components/LanguageDropdown.vue';
import { reactive } from 'vue';

defineProps({
  title: String,
  description: {
    type: String,
    default: ''
  }
});

const state = reactive({
  mobileMenuVisible: false
});

const errors = Object.values(usePage().props.errors ?? {});
const activeRoute = (route_) => Boolean(route().current(route_));
</script>

<template>
  <Head>
    <title>{{ title }} - {{ usePage().props.branding.name }}</title>
    <meta
      v-if="description.length"
      head-key="description"
      name="description"
      :content="description"
    />
  </Head>
  <Banner
    v-if="errors.length"
    :style="'danger'"
    :message="errors.join('<br>')"
  />
  <div
    v-if="usePage().props.internationalise"
    class="mx-auto w-full max-w-(--breakpoint-xl)"
  >
    <div class="flex justify-end">
      <LanguageDropdown />
    </div>
  </div>
  <SiteContainer :class="{ 'mt-8': !usePage().props.internationalise }">
    <SiteHeader
      @toggle-mobile-menu="(isOpen) => (state.mobileMenuVisible = isOpen)"
    />
    <NavBar
      class="bg-foreground"
      :class="
        state.mobileMenuVisible ? '!mt-0 rounded-b !pt-0 !pb-2' : 'hidden'
      "
    >
      <NavBarItem :href="getRoute('score')" :active="activeRoute('score.*')">
        {{ $t('common.nav.know_your_score') }}
      </NavBarItem>
      <NavBarItem
        :href="getRoute('domain-analysis')"
        :active="activeRoute('domain-analysis.*')"
      >
        {{ $t('common.nav.domain_analysis') }}
      </NavBarItem>
      <Dropdown
        align="middle"
        :active="activeRoute('lookup*') || activeRoute('explain.*')"
      >
        <template #trigger>{{ $t('common.nav.lookup') }}</template>
        <template #content>
          <DropdownLink :href="getRoute('lookup', ['dns'])">
            {{ $t('common.nav.lookup_sub.dns') }}
          </DropdownLink>
          <DropdownLink :href="route('lookup.blocklist.index')">
            {{ $t('common.nav.lookup_sub.blocklist') }}
          </DropdownLink>
        </template>
      </Dropdown>
      <NavBarItem
        :href="route('header-analysis.index')"
        :active="activeRoute('header-analysis.index')"
      >
        {{ $t('common.nav.header_analysis') }}
      </NavBarItem>
      <NavBarItem
        :href="route('spf-policy-test.index')"
        :active="activeRoute('spf-policy-test.*')"
      >
        {{ $t('common.nav.spf_policy_test') }}
      </NavBarItem>
      <Dropdown
        align="right"
        :active="activeRoute('cidr.*') || activeRoute('dkim-generator.*')"
      >
        <template #trigger>{{ $t('common.nav.more_tools') }}</template>
        <template #content>
          <DropdownLink :href="route('cidr.index')">
            {{ $t('common.nav.more_tools_sub.cidr_calc') }}
          </DropdownLink>
          <DropdownLink :href="route('dkim-generator.index')">
            {{ $t('common.nav.more_tools_sub.dkim_key_gen') }}
          </DropdownLink>
        </template>
      </Dropdown>
    </NavBar>
    <div class="mt-6 flex flex-col gap-4 md:mt-8 md:gap-8">
      <slot />
    </div>
    <Footer />
  </SiteContainer>
</template>
